import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export type TitleLevel = 'display' | 'title' | 'heading' | 'compact' | 'small' | 'eyebrow';

/** Elements the Title primitive can render as. Constrained to the semantic
 *  options that match a Jacquard typography level: the heading family
 *  (h1–h6), block prose (div, p), and inline (span). Adopters who need a
 *  different element should drop the Title primitive and write their own
 *  element + className combination — the kit's typography classes are
 *  free-standing per `src/css/typography.css`. */
export type TitleAs = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'p' | 'span';

const LEVEL_CLASS: Record<TitleLevel, string> = {
  display: 'j-display',
  title: 'j-title',
  heading: 'j-heading',
  compact: 'j-compact-title',
  small: 'j-small-title',
  eyebrow: 'j-eyebrow-title',
};

/* ADR-014 §3 + ADR-022 semantic-heading guidance: each level has a
   default semantic element. v5.0 ships this map as a BREAKING CHANGE
   from v4 (which rendered <div> for every level). Document migration:

   - level="display"  → h1   (page hero / route-level heading)
   - level="title"    → h2   (page section title)
   - level="heading"  → h3   (subsection)
   - level="compact"  → h4   (sub-subsection)
   - level="small"    → h5   (deeper subdivision)
   - level="eyebrow"  → p    (NOT a heading; presentational overline
                              shown ABOVE a heading; <p> avoids
                              outline pollution)

   Consumers who depended on the v4 <div> behavior must pass
   `as="div"` explicitly. The codemod
   `@jacquard/codemods/title-as-explicit` (planned) inserts `as="div"`
   at every <Title> call site at v5 migration time. */
const LEVEL_DEFAULT_AS: Record<TitleLevel, TitleAs> = {
  display: 'h1',
  title: 'h2',
  heading: 'h3',
  compact: 'h4',
  small: 'h5',
  eyebrow: 'p',
};

/* Public prop interface stays a flat interface so adopter wrappers using
   `interface Props extends TitleProps` continue to compile (the same
   trade-off DisplayNum's PR #67 codex #3 found). Rest props are typed
   as HTMLAttributes<HTMLElement> which covers ~99% of real usage
   (aria-*, id, data-*, role, etc.). Element-specific attributes for
   non-generic h1-h6 elements (none meaningful for Title — headings
   take no element-specific attrs) work via cast at call site if ever
   needed. */
export interface TitleProps
  extends
    StyledProps,
    WithChildren,
    UnsafeStyleProps,
    Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'> {
  level?: TitleLevel;
  /** Override the rendered element. Defaults per level — see
   *  `LEVEL_DEFAULT_AS` in source. Pass `as="div"` to opt out of
   *  semantic heading rendering. */
  as?: TitleAs;
}

/* forwardRef so adopters can attach refs for focus management, scroll-into-view,
   getBoundingClientRect, etc. The ref type is HTMLElement since `as` accepts
   any of h1-h6/div/p/span. Consumers needing a narrower type cast at the
   call site: <Title<HTMLHeadingElement> ref={...} /> — but the kit-internal
   API doesn't expose a generic so the simpler signature ships. */
export const Title = React.forwardRef<HTMLElement, TitleProps>(function Title(
  { children, level = 'heading', as, className, unsafe_style, ...rest },
  ref,
) {
  const cls = LEVEL_CLASS[level] ?? 'j-heading';
  // Same defensive fallback as cls — an unknown level (bypassed TS) falls
  // back to the heading default rather than throwing "Element type is invalid".
  const defaultAs = LEVEL_DEFAULT_AS[level] ?? LEVEL_DEFAULT_AS.heading;
  const Element = (as ?? defaultAs) as React.ElementType;
  return (
    <Element ref={ref} {...rest} className={cx(cls, className)} style={unsafe_style}>
      {children}
    </Element>
  );
});
